import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { apiRequest } from '../helpers/api';
import { User } from './auth';
import { Comment } from './comment';
import { Tag } from './tag';

export interface Post {
  _id: string;
  authorId: User | string;
  title: string;
  description?: string;
  tags: Tag[] | string[];
  url: string;
  summary: string;

  authorArticle?: string;
  datePublished?: string;
  createdAt: Date;
  updatedAt: Date;
  image?: string;
  logo?: string;
  publisher?: string;
  lang?: string;

  likes: string[];
  comments: Comment[] | string[];
}

export interface CreatePostDto {
  microsoftTeamId: string;

  title: string;
  description?: string;
  tags: string[];
  url: string;
  summary: string;

  authorArticle?: string;
  datePublished?: string;
  image?: string;
  logo?: string;
  publisher?: string;
  lang?: string;
}

interface ParamsQuery {
  searchPost: string;
  offset?: number;
  limit?: number;
}

export interface SearchResultDto {
  total: number;
  posts: Post[];
}

export const createNewPost = createAsyncThunk(
  'posts/createNewPost',
  async (newPost: CreatePostDto) => {
    return await apiRequest<Post>('POST', '/posts', undefined, {
      microsoftTeamId: newPost.microsoftTeamId,

      title: newPost.title,
      description: newPost?.description,
      tags: newPost.tags,
      url: newPost.url,
      summary: newPost.summary,

      authorArticle: newPost?.authorArticle,
      datePublished: newPost?.datePublished,
      image: newPost?.image,
      logo: newPost?.logo,
      publisher: newPost?.publisher,
      lang: newPost?.lang,
    });
  },
);

export const fetchPostsByTagsId = createAsyncThunk(
  'posts/fetchPostsByTagsId',
  async (tags: string[]) => {
    const tagsQuery = tags.map((tag) => `tags[]=${tag}`).join('&');
    return await apiRequest<Post[]>('GET', `/posts/find?${tagsQuery}`);
  },
);

export const fetchPostByPostId = createAsyncThunk(
  'posts/fetchPostByPostId',
  async (postId: string) => {
    return await apiRequest<Post>('GET', `/posts/${postId}`);
  },
);

export const fetchPostsBySearch = createAsyncThunk(
  'posts/fetchPostsBySearch',
  async ({
    microsoftTeamId,
    paramsQuery,
  }: {
    microsoftTeamId: string;
    paramsQuery: ParamsQuery;
  }) => {
    const offsetQuery: string =
      paramsQuery.offset === undefined ? '' : `&offset=${paramsQuery.offset}`;
    const limitQuery: string =
      paramsQuery.limit === undefined ? '' : `&limit=${paramsQuery.limit}`;

    return await apiRequest<SearchResultDto>(
      'GET',
      `/posts/microsoft/${microsoftTeamId}?search=${paramsQuery.searchPost}${offsetQuery}${limitQuery}`,
    );
  },
);

export const addLikeToPost = createAsyncThunk(
  'posts/addLikeToPost',
  async (postId: string) => {
    const result = await apiRequest<Post>('POST', `/posts/${postId}/like`);

    return { id: result._id, changes: { likes: result.likes } };
  },
);

export const removeLikeOfPost = createAsyncThunk(
  'posts/removeLikeOfPost',
  async (postId: string) => {
    const result = await apiRequest<Post>('DELETE', `/posts/${postId}/like`);

    return { id: result._id, changes: { likes: result.likes } };
  },
);

export const postsAdapter = createEntityAdapter<Post>({
  selectId: (post: Post) => post._id,
});

const postSlice = createSlice({
  name: 'posts',
  initialState: postsAdapter.getInitialState(),
  reducers: {
    removeAllPosts: (state) => {
      postsAdapter.removeAll(state);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPostsByTagsId.fulfilled, postsAdapter.setAll);
    builder.addCase(fetchPostByPostId.fulfilled, postsAdapter.setOne);
    builder.addCase(fetchPostsBySearch.fulfilled, (state, action) => {
      postsAdapter.setAll(state, action.payload.posts);
    });
    builder.addCase(addLikeToPost.fulfilled, postsAdapter.updateOne);
    builder.addCase(removeLikeOfPost.fulfilled, postsAdapter.updateOne);
    builder.addCase(createNewPost.fulfilled, postsAdapter.upsertOne);
  },
});

export const { removeAllPosts } = postSlice.actions;

export default postSlice.reducer;
